import React from 'react';
import { Card, CardContent, Typography, Box, Chip, useTheme } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { 
  AccountBalanceWallet, 
  TrendingUp, 
  AccessTime, 
  Lightbulb,
  ArrowUpward,
  Schedule
} from '@mui/icons-material';

// Animations
const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-5px);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
`;

// Modern glassmorphism card with enhanced styling
const ModernStatsCard = styled(Card)(({ theme }) => ({
  height: '100%',
  minHeight: '180px',
  background: theme.palette.mode === 'light' 
    ? 'rgba(255, 255, 255, 0.85)'
    : 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(20px)',
  border: theme.palette.mode === 'light'
    ? '1px solid rgba(255, 255, 255, 0.3)'
    : '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: '24px',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: theme.palette.mode === 'light'
      ? '0 25px 50px rgba(0, 0, 0, 0.15)'
      : '0 25px 50px rgba(0, 0, 0, 0.5)',
    '& .icon-container': {
      transform: 'scale(1.1)',
      animation: `${float} 2s ease-in-out infinite`,
    },
    '& .value-text': {
      transform: 'scale(1.05)',
      textShadow: theme.palette.mode === 'light'
        ? '0 2px 8px rgba(0,0,0,0.2)'
        : '0 2px 8px rgba(0,0,0,0.5)',
    },
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '100px',
    height: '100px',
    background: theme.palette.mode === 'light'
      ? 'radial-gradient(circle, rgba(56, 178, 172, 0.1) 0%, transparent 70%)'
      : 'radial-gradient(circle, rgba(79, 209, 199, 0.2) 0%, transparent 70%)',
    borderRadius: '50%',
    transform: 'translate(30px, -30px)',
    transition: 'all 0.3s ease',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '60px',
    height: '60px',
    background: theme.palette.mode === 'light'
      ? 'radial-gradient(circle, rgba(26, 54, 93, 0.1) 0%, transparent 70%)'
      : 'radial-gradient(circle, rgba(66, 153, 225, 0.2) 0%, transparent 70%)',
    borderRadius: '50%',
    transform: 'translate(-20px, 20px)',
  },
}));

// Modern icon container with gradient backgrounds
const ModernIconContainer = styled(Box)<{ color: string; gradient: string }>(({ color, gradient, theme }) => ({
  width: '56px',
  height: '56px',
  borderRadius: '18px',
  background: gradient,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '20px',
  position: 'relative',
  boxShadow: theme.palette.mode === 'light'
    ? `0 8px 20px ${color}30`
    : `0 8px 20px ${color}50`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: '2px',
    borderRadius: '16px',
    background: theme.palette.mode === 'light'
      ? 'rgba(255, 255, 255, 0.9)'
      : 'rgba(255, 255, 255, 0.1)',
  },
  '& svg': {
    color: color,
    fontSize: '28px',
    position: 'relative',
    zIndex: 1,
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
  },
}));

// Enhanced value text with gradient
const ModernValueText = styled(Typography)(({ theme }) => ({
  fontSize: '2.5rem',
  fontWeight: 800,
  lineHeight: 1,
  marginBottom: '8px',
  background: theme.palette.mode === 'light'
    ? 'linear-gradient(135deg, #1A365D 0%, #2C5282 50%, #38B2AC 100%)'
    : 'linear-gradient(135deg, #63B3ED 0%, #4299E1 50%, #4FD1C7 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  textShadow: 'none', // Remove text shadow as it conflicts with transparent text
  fontFamily: '"SF Pro Display", "Inter", sans-serif',
  transition: 'all 0.3s ease',
  // Fallback for browsers that don't support background-clip
  '@supports not (background-clip: text)': {
    color: theme.palette.mode === 'light' ? '#1A365D' : '#63B3ED',
    background: 'none',
    WebkitTextFillColor: 'initial',
  },
  letterSpacing: '-0.02em',
}));

// Modern title with better typography
const ModernTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  fontWeight: 700,
  marginBottom: '12px',
  color: theme.palette.text.primary,
  letterSpacing: '-0.01em',
  fontFamily: '"SF Pro Display", "Inter", sans-serif',
}));

// Enhanced subtitle with icon
const ModernSubtitle = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  '& .MuiTypography-root': {
    fontSize: '0.85rem',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    letterSpacing: '0.01em',
  },
  '& .trend-icon': {
    fontSize: '16px',
    color: theme.palette.success.main,
    animation: `${pulse} 2s ease-in-out infinite`,
  },
}));

interface StatsSummaryProps {
  totalPoints: number;
  totalValue: number;
  totalValueUSD?: number;
  exchangeRate?: number;
  lastUpdated?: Date;
  pointsExpiringSoon: number;
  recommendations: number;
}

export const StatsSummary: React.FC<StatsSummaryProps> = ({
  totalPoints,
  totalValue,
  totalValueUSD,
  exchangeRate,
  lastUpdated,
  pointsExpiringSoon,
  recommendations,
}) => {
  const theme = useTheme();
  
  const formatNumber = (num: number): string => {
    if (isNaN(num) || num === null || num === undefined) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
  };

  const formatCurrency = (amount: number): string => {
    if (isNaN(amount) || amount === null || amount === undefined) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatUSD = (amount: number): string => {
    if (isNaN(amount) || amount === null || amount === undefined) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Create portfolio value subtitle with real exchange rate
  const getPortfolioSubtitle = (): string => {
    if (totalValueUSD && exchangeRate) {
      const timeAgo = lastUpdated ? ` (${new Date(lastUpdated).toLocaleTimeString()})` : '';
      return `${formatUSD(totalValueUSD)} @ ₹${exchangeRate}${timeAgo}`;
    }
    return 'Current market value';
  };

  const stats = [
    {
      title: 'Total Points',
      value: formatNumber(totalPoints),
      icon: AccountBalanceWallet,
      color: '#3182CE',
      gradient: 'linear-gradient(135deg, #3182CE 0%, #63B3ED 100%)',
      subtitle: 'Across all programs',
      trend: '+12%',
    },
    {
      title: 'Estimated Value',
      value: formatCurrency(totalValue),
      icon: TrendingUp,
      color: '#38A169',
      gradient: 'linear-gradient(135deg, #38A169 0%, #68D391 100%)',
      subtitle: getPortfolioSubtitle(),
      trend: '+8%',
    },
    {
      title: 'Expiring Soon',
      value: formatNumber(pointsExpiringSoon),
      icon: AccessTime,
      color: pointsExpiringSoon > 0 ? '#E53E3E' : '#38A169',
      gradient: pointsExpiringSoon > 0 
        ? 'linear-gradient(135deg, #E53E3E 0%, #FC8181 100%)'
        : 'linear-gradient(135deg, #38A169 0%, #68D391 100%)',
      subtitle: 'Points expiring in 30 days',
      urgent: pointsExpiringSoon > 0,
    },
    {
      title: 'AI Recommendations',
      value: recommendations.toString(),
      icon: Lightbulb,
      color: '#D69E2E',
      gradient: 'linear-gradient(135deg, #D69E2E 0%, #F6E05E 100%)',
      subtitle: 'Smart suggestions available',
      trend: 'New',
    },
  ];

  return (
    <Box sx={{ 
      display: 'grid', 
      gridTemplateColumns: { 
        xs: 'repeat(1, 1fr)', 
        sm: 'repeat(2, 1fr)', 
        md: 'repeat(4, 1fr)' 
      },
      gap: { xs: 2, md: 3 },
      mb: 4 
    }}>
      {stats.map((stat, index) => (
        <ModernStatsCard key={index} className="stats-card">
          <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <ModernIconContainer 
              color={stat.color} 
              gradient={stat.gradient}
              className="icon-container"
            >
              <stat.icon />
            </ModernIconContainer>

            <ModernValueText variant="h4" className="value-text">
              {stat.value}
            </ModernValueText>

            <ModernTitle variant="h6">
              {stat.title}
            </ModernTitle>

            <Box sx={{ mt: 'auto' }}>
              <ModernSubtitle>
                {stat.urgent && (
                  <Chip 
                    label="Urgent" 
                    size="small" 
                    color="error"
                    sx={{ 
                      fontSize: '0.7rem', 
                      height: 20,
                      fontWeight: 600,
                      mr: 1,
                      animation: `${pulse} 2s ease-in-out infinite`,
                    }}
                  />
                )}
                <Typography>{stat.subtitle}</Typography>
                {stat.trend && (
                  <>
                    <ArrowUpward className="trend-icon" />
                    <Typography 
                      sx={{ 
                        color: 'success.main', 
                        fontWeight: 600,
                        fontSize: '0.8rem',
                      }}
                    >
                      {stat.trend}
                    </Typography>
                  </>
                )}
              </ModernSubtitle>
            </Box>
          </CardContent>
        </ModernStatsCard>
      ))}
    </Box>
  );
};